import every from "lodash/every";
import forEach from "lodash/forEach";
import isUndefined from "lodash/isUndefined";
import join from "lodash/join";
import split from "lodash/split";
import zip from "lodash/zip";

interface ParamsType {
  [key: string]: string | null | undefined;
}

interface ExtractedParamsType {
  [key: string]: string;
}

export const buildQuery = (params: ParamsType): string => {
  const serializedPairs: string[] = [];

  forEach(params, (v, k) => {
    if (v && v !== "") {
      serializedPairs.push(`${k}=${encodeURIComponent(v)}`);
    }
  });

  serializedPairs.sort();

  if (serializedPairs.length === 0) {
    return "";
  }

  return "?" + join(serializedPairs, "&");
};

export const extractParams = (source: string, path: string): ExtractedParamsType => {
  if (!pathMatch(source, path, false)) {
    return {};
  }

  const sourceParts = split(source, "/");
  const pathParts = split(path, "/");
  const pairs = zip(sourceParts, pathParts);

  const params: ExtractedParamsType = {};

  forEach(pairs, (pair) => {
    const [sourcePart, pathPart] = pair;

    if (sourcePart && pathPart && sourcePart[0] === ":" && pathPart !== "") {
      const name = sourcePart.slice(1, 10000);
      params[name] = pathPart;
    }
  });

  return params;
};

export const extractQueryParams = (query: string): ExtractedParamsType => {
  if (query.length <= 1) {
    return {};
  }

  if (query[0] !== "?") {
    throw new Error("Query must begin by `?`");
  }

  const params: ExtractedParamsType = {};

  const parts = split(query.slice(1, 100000), "&");
  forEach(parts, (part) => {
    const [param, value] = split(part, "=", 2);

    if (value && value !== "") {
      params[param] = value;
    }
  });

  return params;
};

export const pathMatch = (source: string, path: string, exact: boolean): boolean => {
  const sourceParts = split(source, "/");
  const pathParts = split(path, "/");
  const pairs = zip(sourceParts, pathParts);

  const matched = every(pairs, (pair) => {
    const [sourcePart, pathPart] = pair;

    if (sourcePart === "" && pathPart === "") {
      return true;
    }

    if (isUndefined(sourcePart)) {
      return true;
    }

    if (sourcePart[0] === ":" && pathPart !== "") {
      return true;
    }

    return sourcePart === pathPart;
  });

  if (!matched) {
    return false;
  }

  if (exact && sourceParts.length !== pathParts.length) {
    return false;
  }

  return true;
};
